/*
 * Очередь отправки: что человек уже отправил, но до сервера оно ещё не
 * доехало. Здесь только переходы состояний — ни React, ни сети, поэтому
 * очередь проверяется тестами целиком.
 *
 * Очередь живёт в ChatProvider, а не в поле ввода: уход в другой чат или
 * раздел не должен обрывать загрузку видео (решение Ильи 15.09.2026).
 */

/** Ждёт своей очереди, идёт сейчас, сорвалось и ждёт решения человека. */
export type DraftState = 'ждёт' | 'идёт' | 'сорвалось';

export type DraftFile = {
  key: string;
  file: File;
  name: string;
  mime: string;
  size: number;
  /** Адрес для показа картинки до отправки; отзывается, когда черновик ушёл. */
  localUrl: string;
};

export type Draft = {
  key: string;
  channelId: string;
  /** Ответ в ветке: черновик живёт в ветке, а не в основной ленте. */
  parentId: number | null;
  replyToId?: number;
  text: string;
  files: DraftFile[];
  ts: number;
  state: DraftState;
  /** Какой файл идёт сейчас, считая с нуля. */
  fileIndex: number;
  /** Ход текущего файла, 0…100. */
  pct: number;
  /** Номера уже загруженных на сервер файлов по порядку: повтор их не заливает заново. */
  uploaded: number[];
  error: string;
  /** Голосовое: длительность и форма волны снимаются при записи, не из файла. */
  voice?: { seconds: number; waveform: number[] };
};

/** Точечная правка одного черновика: неизвестный ключ очередь не меняет. */
const patch = (queue: Draft[], key: string, next: (d: Draft) => Draft): Draft[] =>
  queue.map((d) => (d.key === key ? next(d) : d));

export const enqueue = (queue: Draft[], draft: Draft): Draft[] => [...queue, draft];

export const draftsOf = (queue: Draft[], channelId: string, parentId: number | null): Draft[] =>
  queue.filter((d) => d.channelId === channelId && d.parentId === parentId);

/**
 * Кого отправлять следующим. По одному за раз: иначе два видео делят канал
 * и ползут оба.
 */
export const nextDraft = (queue: Draft[]): Draft | null => {
  if (queue.some((d) => d.state === 'идёт')) return null;
  return queue.find((d) => d.state === 'ждёт') ?? null;
};

export const beginFile = (queue: Draft[], key: string, index: number): Draft[] =>
  patch(queue, key, (d) => ({ ...d, state: 'идёт', fileIndex: index, pct: 0 }));

export const progress = (queue: Draft[], key: string, pct: number): Draft[] =>
  patch(queue, key, (d) => ({ ...d, pct }));

export const fail = (queue: Draft[], key: string, error: string): Draft[] =>
  patch(queue, key, (d) => ({ ...d, state: 'сорвалось', error }));

/** Файл дошёл до сервера: запоминаем его номер, чтобы повтор начал со следующего. */
export const fileDone = (queue: Draft[], key: string, fileId: number): Draft[] =>
  patch(queue, key, (d) => ({ ...d, uploaded: [...d.uploaded, fileId] }));

export const retry = (queue: Draft[], key: string): Draft[] =>
  patch(queue, key, (d) => ({
    ...d, state: 'ждёт', error: '', pct: 0, fileIndex: d.uploaded.length,
  }));

/**
 * Очередь с устройства после перезапуска. Что было в пути, не доехало: сеть
 * оборвалась вместе с закрытием, поэтому «ждёт» и «идёт» становятся «не
 * отправлено» — автоповтора нет, решает человек (решение 15.09). Адрес для
 * показа картинки умер вместе со страницей — делаем новый из самого файла.
 */
export const restore = (drafts: Draft[]): Draft[] =>
  drafts.map((d) => {
    const files = d.files.map((f) => ({
      ...f,
      localUrl: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(f.file) : '',
    }));
    const interrupted = d.state === 'ждёт' || d.state === 'идёт';
    return interrupted
      ? { ...d, files, state: 'сорвалось', error: 'Не отправлено', pct: 0, fileIndex: d.uploaded.length }
      : { ...d, files, pct: 0 };
  });

export const drop = (queue: Draft[], key: string): Draft[] =>
  queue.filter((d) => d.key !== key);
